import React, { useState, useEffect, useMemo } from 'react'
import { UserPlus, Award, CheckCircle, Users, Loader2 } from 'lucide-react'
import { getAllMonumentaEmployees } from '@/lib/real-data-service'

export function RecentActivity() {
  const [employees, setEmployees] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load employees data
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const employeesData = await getAllMonumentaEmployees()
        setEmployees(employeesData)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load employees')
        console.error('Error loading employees for RecentActivity:', err)
      } finally {
        setIsLoading(false)
      }
    }

    loadEmployees()
  }, [])

  // Generate activities from real employee data
  const activities = useMemo(() => {
    if (!employees.length) return []

    const actions = [
      { action: 'Novo colaborador contratado', icon: UserPlus, iconColor: 'text-success' },
      { action: 'Treinamento concluído', icon: Award, iconColor: 'text-brand-primary' },
      { action: 'Documento assinado', icon: CheckCircle, iconColor: 'text-success' },
      { action: 'Processo seletivo iniciado', icon: Users, iconColor: 'text-info' }
    ]
    const timeOptions = ['2 horas atrás', '4 horas atrás', '1 dia atrás', '2 dias atrás']

    return actions.map((actionData, index) => {
      const randomEmployee = employees[Math.floor(Math.random() * employees.length)]
      return {
        id: index + 1,
        ...actionData,
        user: randomEmployee.name,
        time: timeOptions[index]
      }
    })
  }, [employees])

  return (
    <div className="card">
      <h3 className="font-display text-base sm:text-lg font-semibold text-white mb-6 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
        Atividade Recente
      </h3>
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-brand-primary animate-spin mr-3" />
            <span className="text-brand-gray text-sm font-body">Carregando atividades...</span>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <Users className="w-8 h-8 text-red-400 mx-auto mb-2" />
            <p className="text-red-400 text-sm font-body">Erro ao carregar atividades</p>
            <p className="text-brand-gray text-xs font-body mt-1">{error}</p>
          </div>
        ) : activities.length > 0 ? (
          activities.map((activity) => {
            const Icon = activity.icon
            return (
              <div key={activity.id} className="flex items-start space-x-4 p-3 rounded-monumenta hover:bg-brand-dark-lighter transition-all duration-200 group animate-fade-in-up">
                <div className="p-2 rounded-monumenta bg-brand-primary/10 group-hover:bg-brand-primary/20 transition-colors duration-200">
                  <Icon className={`${activity.iconColor} flex-shrink-0 group-hover:scale-110 transition-transform duration-200`} size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-body leading-relaxed">
                    <span className="font-display font-medium">{activity.user}</span>
                    <span className="text-brand-gray-light"> • {activity.action}</span>
                  </p>
                  <p className="text-xs text-brand-gray font-body mt-1">{activity.time}</p>
                </div>
              </div>
            )
          })
        ) : (
          <div className="text-center py-8">
            <Users className="w-8 h-8 text-brand-gray mx-auto mb-2" />
            <p className="text-brand-gray text-sm font-body">Nenhuma atividade encontrada</p>
          </div>
        )}
      </div>
    </div>
  )
}